import {
  getBrowserStorage,
  safeStorageGet,
  safeStorageSet,
  safeStorageRemove,
  parseStoredValue,
  serializeStoredValue,
} from "@catchy/region-config/storage-safety";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";

const isServerSide = () => typeof window === "undefined";

export function createUseLocalStorage({ getStorageKey, postStorageMessage }) {
  return function useLocalStorage(key, defaultValue = null) {
    const defaultValueRef = useRef(defaultValue);
    defaultValueRef.current = defaultValue;

    const canonicalKey = useMemo(() => getStorageKey(key), [key]);
    const [isHydrated, setIsHydrated] = useState(false);
    const hydrationRef = useRef(0);

    const { data: value = defaultValueRef.current, mutate } = useSWR(
      canonicalKey,
      null,
      {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        refreshWhenHidden: false,
        refreshWhenOffline: false,
      },
    );

    const readStoredValue = useCallback(
      (fallback) => {
        if (isServerSide()) return fallback;

        const storedValue = safeStorageGet(getBrowserStorage(), canonicalKey);
        if (storedValue === null || storedValue === "undefined")
          return fallback;

        const checked = parseStoredValue(canonicalKey, storedValue, fallback);
        return checked.ok ? checked.value : fallback;
      },
      [canonicalKey],
    );

    useEffect(() => {
      const hydration = hydrationRef.current + 1;
      hydrationRef.current = hydration;
      let active = true;
      setIsHydrated(false);

      if (isServerSide()) return () => {};

      void mutate(readStoredValue(defaultValueRef.current), {
        revalidate: false,
      }).then(() => {
        if (active && hydrationRef.current === hydration) {
          setIsHydrated(true);
        }
      });

      return () => {
        active = false;
      };
    }, [canonicalKey, mutate, readStoredValue]);

    const setValue = useCallback(
      async (valueOrUpdater) => {
        const resolveValue = (current) =>
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(current)
            : valueOrUpdater;
        let resolvedValue = null;

        await mutate(
          (current) => {
            const fromStorage = readStoredValue(defaultValueRef.current);
            const candidate = resolveValue(
              current ?? fromStorage ?? defaultValueRef.current ?? null,
            );
            const checked = serializeStoredValue(
              canonicalKey,
              candidate,
              defaultValueRef.current,
            );
            if (!checked.ok) return current ?? fromStorage;
            resolvedValue = checked;
            return checked.value;
          },
          { revalidate: false },
        );

        if (isServerSide() || !resolvedValue) return;
        if (
          !safeStorageSet(getBrowserStorage(), canonicalKey, resolvedValue.raw)
        )
          return;
        try {
          postStorageMessage?.({
            key: canonicalKey,
            value: resolvedValue.raw,
            action: "set",
          });
        } catch {
          /* A missing native bridge must not break browser state. */
        }
      },
      [canonicalKey, mutate, readStoredValue],
    );

    const removeValue = useCallback(async () => {
      await mutate(defaultValueRef.current, { revalidate: false });
      if (isServerSide()) return;

      if (!safeStorageRemove(getBrowserStorage(), canonicalKey)) return;
      try {
        postStorageMessage?.({
          key: canonicalKey,
          value: "null",
          action: "remove",
        });
      } catch {
        /* Keep local removal usable if the native bridge fails. */
      }
    }, [canonicalKey, mutate]);

    return [value, setValue, removeValue, isHydrated];
  };
}
